import {
  resetUploadBox,
  type UploadUiStatus,
} from "../../schema/handler-functions";
import { Spinner, Text, Flex, Button } from "@chakra-ui/react";
import { Check, UploadCloud, X } from "lucide-react";
import { AppColors } from "@/styles/colors";
import { TaxUploadHistoryItem } from "../../schema/taxes.schema";
import { Dispatch, SetStateAction } from "react";
import { uploadAnotherButtonProps } from "../../schema/custom-styles";

interface Props {
  uploadUiStatus: UploadUiStatus;
  processingFileName?: string | null;
  lastUploadedFileName?: string | null;
  uploadResultMessage?: string | null;
  history?: TaxUploadHistoryItem[];
  setSelectedForDetail: (item: TaxUploadHistoryItem) => void;
  setProcessingFileName: Dispatch<SetStateAction<string | null>>;
  setLastUploadedFileName: Dispatch<SetStateAction<string | null>>;
  setUploadResultMessage: Dispatch<SetStateAction<string | null>>;
  setUploadUiStatus: Dispatch<SetStateAction<UploadUiStatus>>;
  setOpenErrors: (open: boolean) => void;
  openFilePicker: () => void;
  setCurrentTab: (tab: string) => void;
}

export const UploadBoxContent = ({
  uploadUiStatus,
  processingFileName,
  lastUploadedFileName,
  uploadResultMessage,
  history,
  setSelectedForDetail,
  setOpenErrors,
  openFilePicker,
  setProcessingFileName,
  setLastUploadedFileName,
  setUploadResultMessage,
  setUploadUiStatus,
  setCurrentTab,
}: Props) => {
  switch (uploadUiStatus) {
    case "processing":
      return (
        <>
          <Spinner />
          <Text fontWeight={700}>Procesando archivo</Text>
          <Text>{processingFileName}</Text>
          <Text color={AppColors.GRAY_TEXT}>Validando estructura y datos</Text>
        </>
      );
    case "recordErrors":
      return (
        <>
          <X color={AppColors.ERROR_TEXT} />
          <Text fontWeight={700} color={AppColors.ERROR_TEXT}>
            Errores en registros
          </Text>
          <Text color={AppColors.ERROR_TEXT}>{lastUploadedFileName}</Text>
          <Text color={AppColors.ERROR_TEXT}>{uploadResultMessage}</Text>

          <Flex gap="12px" pt="8px">
            <Button
              paddingX="16px"
              bgColor={AppColors.BLACK_TEXT_SECONDARY}
              color={AppColors.WHITE_SECONDARY}
              onClick={() => {
                const firstWithErrors = history?.find(
                  (h) => (h.errores?.length ?? 0) > 0,
                );
                if (firstWithErrors) {
                  setSelectedForDetail(firstWithErrors);
                }
                setOpenErrors(true);
              }}
            >
              Ver detalle de errores
            </Button>
            <Button
              {...uploadAnotherButtonProps}
              onClick={() => {
                resetUploadBox({
                  setProcessingFileName,
                  setLastUploadedFileName,
                  setUploadResultMessage,
                  setUploadUiStatus,
                });
                openFilePicker();
              }}
            >
              Subir otro archivo
            </Button>
          </Flex>
        </>
      );
    case "invalid":
      return (
        <>
          <X color={AppColors.ERROR_TEXT} />
          <Text fontWeight={700} color={AppColors.ERROR_TEXT}>
            Archivo inválido
          </Text>
          <Text color={AppColors.ERROR_TEXT}>{lastUploadedFileName}</Text>
          <Text color={AppColors.ERROR_TEXT}>{uploadResultMessage}</Text>
          <Button
            {...uploadAnotherButtonProps}
            onClick={() => {
              resetUploadBox({
                setProcessingFileName,
                setLastUploadedFileName,
                setUploadResultMessage,
                setUploadUiStatus,
              });
              openFilePicker();
            }}
          >
            Subir otro archivo
          </Button>
        </>
      );
    case "success":
      return (
        <>
          <Check color={AppColors.GREEN_ICON} />
          <Text fontWeight={700}>Archivo procesado correctamente</Text>
          <Text color={AppColors.GRAY_TEXT}>{uploadResultMessage}</Text>

          <Flex gap="12px" pt="8px">
            <Button
              {...uploadAnotherButtonProps}
              onClick={() => {
                resetUploadBox({
                  setProcessingFileName,
                  setLastUploadedFileName,
                  setUploadResultMessage,
                  setUploadUiStatus,
                });
                openFilePicker();
              }}
            >
              Subir otro archivo
            </Button>
            <Button
              bgColor={AppColors.BLACK_TEXT_SECONDARY}
              color={AppColors.WHITE_SECONDARY}
              onClick={() => setCurrentTab("ImpuestosCargados")}
              paddingX="16px"
              borderRadius="8px"
            >
              Ver impuestos cargados
            </Button>
          </Flex>
        </>
      );
    default:
      return (
        <>
          <UploadCloud />
          <Text fontWeight={700}>Seleccioná el archivo o arrastralo</Text>
          <Text color={AppColors.GRAY_TEXT}>
            Solo archivos .csv - Peso Máximo 10MB
          </Text>
          <Button
            bgColor={AppColors.GREEN_PRIMARY}
            color={AppColors.WHITE_SECONDARY}
            onClick={openFilePicker}
            paddingX="16px"
          >
            Seleccionar archivo
          </Button>
        </>
      );
  }
};
